import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { authRoles } from 'app/auth';

/** The staff Container Shipments screen (BackofficeConfig: `shipments/containers`), all four tabs. */
const CONTAINER_SHIPMENTS_PATH = /^\/shipments\/containers\/?$/;

/**
 * Container status while staff review product photos (Awaiting Approval tab).
 * Orders under that container are at Pending Product Image Review — that is order status,
 * not what this list row carries. Container-level Pending is factory still uploading; keep
 * the icon off there (empty-container hiding is 3.0.6).
 */
export const WAITING_FOR_PRODUCT_IMAGE_APPROVAL_CODE = 'ORDER_WAITING_FOR_PRODUCT_IMAGE_APPROVAL';

/** statuses_types.name: inserted 2025-07-29, renamed 2025-08-14 (StatusConstants). */
const WAITING_FOR_PRODUCT_IMAGE_APPROVAL_NAMES = new Set([
  'Waiting for Product Image Approval',
  'Waiting-Product Image Approval',
]);

/** True when the container row is at Waiting for Product Image Approval. */
export const isWaitingForProductImageApproval = ({ statusCode, statusName } = {}) => {
  if (statusCode === WAITING_FOR_PRODUCT_IMAGE_APPROVAL_CODE) return true;
  if (statusName && WAITING_FOR_PRODUCT_IMAGE_APPROVAL_NAMES.has(statusName)) return true;
  return false;
};

/**
 * PGSS-5672: container statuses that come after the product images were approved, where staff can
 * still look at them (read-only). Approving moves a container to ORDER_IN_PRODUCTION_2, but that status
 * is also used before any image is uploaded, so it is left out; from In-Loading on the container has
 * certainly been through review.
 */
export const VIEW_ONLY_PRODUCT_IMAGE_STATUS_CODES = new Set([
  'INLOADING_SHIPMENT',
  'ORDER_PENDING_FOR_RECEIVE_SHIPMENT',
  'SHIPMENT_COMPLETED',
]);

/**
 * statuses_types.name fallbacks for when the row carries no system_code: 'Inloading Shipment' is the
 * seeded name of INLOADING_SHIPMENT (2025-08-22 changelog), the others are StatusConstants names.
 */
const VIEW_ONLY_PRODUCT_IMAGE_STATUS_NAMES = new Set([
  'Inloading Shipment',
  'Pending for Receive Shipment',
  'Shipment Completed',
]);

/** True when the container is past image approval (loading, shipped, received). */
export const isAfterProductImageApproval = ({ statusCode, statusName } = {}) => {
  if (statusCode && VIEW_ONLY_PRODUCT_IMAGE_STATUS_CODES.has(statusCode)) return true;
  if (!statusCode && statusName && VIEW_ONLY_PRODUCT_IMAGE_STATUS_NAMES.has(statusName)) return true;
  return false;
};

/**
 * PGSS-5672: whether Review Product Images may offer its status actions (Approve Shipment, Request
 * Revision) for this container. Those actions exist only at Waiting for Product Image Approval;
 * opened at any later status, the page is view-only. `status` is the page's shipment.status.
 */
export const productImagesReviewIsActionable = (status) =>
  isWaitingForProductImageApproval({ statusCode: status?.system_code, statusName: status?.name });

/**
 * PGSS-5623 / PGSS-5637: where a container row's "View product images" link goes, or null when it
 * shouldn't show.
 *
 * Gate on the *container* status from the list row (ShipmentTabResponse): Waiting for Product
 * Image Approval (to review), or PGSS-5672 any status after approval (to look back, read-only).
 * Do not use order status (Pending Product Image Review) or container-level Pending (factory
 * still uploading).
 *
 * Only the roles that can open Review Product Images (StaffAppConfig: authRoles.staff = admin, staff, sales).
 * Container Shipments also admits warehouse employee and warehouse lead, who would get a link to a page
 * their role is refused.
 */
export const productImagesPath = ({ pathname, role, shipmentId, statusCode, statusName }) => {
  if (!authRoles.staff.includes(role) || !shipmentId) return null;
  if (!CONTAINER_SHIPMENTS_PATH.test(pathname || '')) return null;
  const status = { statusCode, statusName };
  if (!isWaitingForProductImageApproval(status) && !isAfterProductImageApproval(status)) return null;
  return `/shipments/reviewProductImages/${shipmentId}`;
};

export default function ProductImagesLink({ to }) {
  if (!to) return null;
  return (
    <Tooltip title="View product images">
      <IconButton className="py-0 px-2" size="small" component={Link} to={to} aria-label="View product images">
        <Icon fontSize="small">photo_library</Icon>
      </IconButton>
    </Tooltip>
  );
}
